import { PrivateContext } from "@/routes/PrivateRoute";
import { useContext, useEffect, useMemo, useState } from "@/common/hooks";
import {
  useGetProductManufacturerInsuranceCompaniesQuery,
  useSaveProductManufacturerInsuranceCompaniesMutation,
} from "@/service/AdminService";
import { useGetInsuranceCompaniesForEnrollmentQuery } from "@/service/APIService";
import {
  ErrorMessage,
  Spinner,
  AgGrid,
  Button,
  Heading,
  InputField,
  SuccessPopup,
  MobileResponsiveGrid,
} from "@/common/components";
import {
  getCurrentDate,
  getFormattedDate,
  scrollToTop,
} from "@/utils/Utilities";

const ProductManufacturerInsuranceCompanies = () => {
  const {
    setNavHeader,
    remove,
    location,
    cancel,
    labels,
    setPageHeader,
    setNavCancelMessage,
    cloneDeep,
    isMobileView
  } = useContext(PrivateContext);

  const [rowDataForAll, setRowDataForAll] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [selectedInsuranceFromAll, setSelectedInsuranceFromAll] = useState([]);
  const [selectedInsurance, setSelectedInsurance] = useState([]);
  const [errors, setErrors] = useState(null);
  const [filterBy, setFilterBy] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [gridApiForAll, setGridApiForAll] = useState({});
  const [gridApiSelected, setGridApiSelected] = useState({});

  const columnDefsForAll = [
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      flex: 0.25,
    },
    {
      headerName: labels?.productManufacturer?.insuranceCompany,
      field: "companyName",
    },
    {
      headerName: labels?.productManufacturer?.insurerTransmitterID,
      field: "id",
    },
    {
      headerName: labels?.productManufacturer?.address,
      field: "addressLine1",
    },
    {
      headerName: labels?.productManufacturer?.cityHeader,
      field: "addressCityName",
    },
    {
      headerName: labels?.productManufacturer?.state,
      field: "addressStateCountry.longName",
    },
    {
      headerName: labels?.productManufacturer?.zipCodeHeader,
      field: "zip",
    },
  ];

  const columnDefs = [
    ...columnDefsForAll,
    {
      headerName: labels?.productManufacturer?.date,
      field: "lastMntDateTime",
      valueGetter: (params) =>
        params.data?.currentDate || params.data?.lastMntDateTime,
      cellRenderer: getFormattedDate,
    },
  ];

  // Toggle for right mobile list (selected)
  const toggleMobileSelect = (id) => {
    setSelectedInsurance((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Toggle for left mobile list (all)
  const toggleMobileSelectFromAll = (id) => {
    setSelectedInsuranceFromAll((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // LEFT mobile columnDefs (All vehicles) - custom checkbox renderer
  const mobileColumnDefsAll = useMemo(() => {
    return columnDefsForAll.map((col) => {
      if (col.field === "companyName") {
        return {
          ...col,
          cellRenderer: (params) => (
            <div className="mr-imc-cell">
              <input
                type="checkbox"
                className="form-check-input mr-row-check mt-4"
                checked={selectedInsuranceFromAll.includes(params.data?.id)}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleMobileSelectFromAll(params.data?.id);
                }}
                aria-label={`Select ${params.value}`}
              />
              <span className="mr-imc-text">{params.value}</span>
            </div>
          ),
        };
      }
      return col;
    });
  }, [columnDefsForAll, selectedInsuranceFromAll]);

  // RIGHT mobile columnDefs (Selected vehicles) - custom checkbox renderer
  const mobileColumnDefsSelected = useMemo(() => {
    return columnDefs.map((col) => {
      if (col.field === "companyName") {
        return {
          ...col,
          cellRenderer: (params) => (
            <div className="mr-imc-cell">
              <input
                type="checkbox"
                className="form-check-input mr-row-check mt-4"
                checked={selectedInsurance.includes(params.data?.id)}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleMobileSelect(params.data?.id);
                }}
                aria-label={`Select ${params.value}`}
              />
              <span className="mr-imc-text">{params.value}</span>
            </div>
          ),
        };
      }
      return col;
    });
  }, [columnDefs, selectedInsurance]);

  // Robust getRowClass for right grid (mobile & desktop)
  const getRowClassSelected = (rowOrParams) => {
    const data =
      (rowOrParams && rowOrParams.data) || rowOrParams || {};
    const id = data?.id ?? data?.data?.id ?? null;
    const selectedIds = Array.isArray(selectedInsurance) ? selectedInsurance : [];
    return `mr-row-rel ${id != null && selectedIds.includes(id) ? "mr-row-selected" : ""}`;
  };

  // Robust getRowClass for left/all grid
  const getRowClassForAll = (rowOrParams) => {
    const data =
      (rowOrParams && rowOrParams.data) || rowOrParams || {};
    const id = data?.id ?? data?.data?.id ?? null;
    const selectedIds = Array.isArray(selectedInsuranceFromAll) ? selectedInsuranceFromAll : [];
    return `mr-row-rel ${id != null && selectedIds.includes(id) ? "mr-row-selected" : ""}`;
  };

  const {
    data: allInsuranceCompanies,
    isFetching,
    error,
  } = useGetInsuranceCompaniesForEnrollmentQuery();

  const {
    data: insuranceCompanies,
    isLoading: isFetching1,
    error: error1,
  } = useGetProductManufacturerInsuranceCompaniesQuery({
    productSponsorId: location?.state?.id,
  });

  const [
    saveProductManufacturerInsuranceCompaniesApi,
    { isLoading: isFetching2, error: error2 },
  ] = useSaveProductManufacturerInsuranceCompaniesMutation();

  const onSelectionChangedForAll = (e) => {
    const selectedRows = e.api.getSelectedRows();
    let array = selectedRows.map((r) => r.id);

    setSelectedInsuranceFromAll([...array]);
    setSelectedInsurance([]); // reset right selection as you did earlier
  };

  const onGridReadyForAll = (params) => {
    setGridApiForAll(params.api);
  };

  // right desktop grid api
  const onGridReadySelected = (params) => {
    setGridApiSelected(params.api);
  };

  const onSelectionChanged = (e) => {
    const selectedRows = e.api.getSelectedRows();
    let array = selectedRows.map((r) => r.id);

    setSelectedInsurance([...array]);
  };

  const addInsurance = () => {
    let allData = cloneDeep(rowDataForAll);
    let currentData = cloneDeep(rowData);

    selectedInsuranceFromAll.forEach((id) => {
      const insurance = allData.find((e1) => e1.id === id);
      if (!insurance) return;
      insurance.currentDate = getCurrentDate();
      remove(allData, (e1) => e1.id === insurance.id);
      currentData.push(insurance);
    });

    setRowDataForAll([...allData]);
    setRowData([...currentData]);
    setFilterBy("");
  };

  const removeInsurance = () => {
    setErrors(null);
    let currentData = cloneDeep(rowData);
    let allData = [];

    for (let i = 0; i < selectedInsurance.length; i++) {
      const insurance = currentData.find(
        (e1) => e1.id === selectedInsurance[i]
      );

      if (insurance?.isRemovable === "false") {
        setErrors({
          errorMessage: labels?.productManufacturer?.insuranceError,
        });
        scrollToTop();
        return;
      }
    }

    selectedInsurance.forEach((id) => {
      const insurance = currentData.find((e1) => e1.id === id);
      if (!insurance) return;
      remove(currentData, (e1) => e1.id === insurance.id);
      allData.push(insurance);
    });

    setRowData([...currentData]);
    setRowDataForAll([...rowDataForAll, ...allData]);
  };

  const updateInsuranceCompanies = () => {
    scrollToTop();

    const payload = {
      productSponsorId: location?.state?.id,
      productSponsorInsuranceCompanyDTOBeans: rowData.map((e) => {
        return {
          insuranceCompany: { id: e.id },
        };
      }),
    };

    saveProductManufacturerInsuranceCompaniesApi(payload)
      .unwrap()
      .then((res) => {
        if (!res.inError) {
          setShowPopup(true);
        }
      });
  };

  useEffect(() => {
    if (rowDataForAll && rowDataForAll.length && gridApiForAll?.getDisplayedRowAtIndex) {
      const node = gridApiForAll.getDisplayedRowAtIndex(0);
      if (node && node.setSelected) node.setSelected(true);
    }
  }, [rowDataForAll, gridApiForAll]);

  useEffect(() => {
    if (
      allInsuranceCompanies &&
      insuranceCompanies &&
      allInsuranceCompanies?.insuranceCompanies &&
      insuranceCompanies?.productSponsorInsuranceCompanyDTOBeans
    ) {
      const insurances =
        insuranceCompanies?.productSponsorInsuranceCompanyDTOBeans?.map((e) => {
          return {
            ...e.insuranceCompany,
            isRemovable: e.isRemovable,
            lastMntDateTime: e.lastMntDateTime,
            lastMntOpId: e.lastMntOpId,
          };
        }) ?? [];
      setRowData(insurances);

      const filteredInsurances =
        allInsuranceCompanies?.insuranceCompanies?.filter(
          (e) => !insurances.find((e1) => e.id === e1.id)
        ) ?? [];

      setRowDataForAll(filteredInsurances);
    }
  }, [allInsuranceCompanies, insuranceCompanies]);

  useEffect(() => {
    setNavHeader(labels?.productManufacturer?.productManufacturer);
    setPageHeader(
      labels?.productManufacturer?.insuranceCompaniesFor + location?.state?.name
    );
    setNavCancelMessage(
      labels?.productManufacturer?.insuranceCompanyCancelMessage
    );
  }, []);

  const filteredRowData = useMemo(() => {
    if (!filterBy) return rowDataForAll;
    return rowDataForAll.filter((e) =>
      (e.companyName || "").toLowerCase().startsWith(filterBy.toLowerCase())
    );
  }, [filterBy, rowDataForAll]);

  const overlayNoRowsTemplate = useMemo(
    () =>
      `<div class="ag-overlay-no-rows-center">${
        labels?.productManufacturer?.noDataInvestmentInsurance ?? "No data"
      }</div>`,
    [labels]
  );

  // Sync parent selection arrays to desktop AgGrid nodes for left (all)
  useEffect(() => {
    try {
      if (!gridApiForAll || !gridApiForAll.forEachNode) return;
      gridApiForAll.forEachNode((node) => {
        const id = node?.data?.id;
        const shouldSelect = Array.isArray(selectedInsuranceFromAll)
          ? selectedInsuranceFromAll.includes(id)
          : false;
        if (node.isSelected() !== shouldSelect) node.setSelected(shouldSelect);
      });
    } catch (e) {
      // silence
    }
  }, [selectedInsuranceFromAll, gridApiForAll]);

  // Sync parent selection arrays to desktop AgGrid nodes for right (selected)
  useEffect(() => {
    try {
      if (!gridApiSelected || !gridApiSelected.forEachNode) return;
      gridApiSelected.forEachNode((node) => {
        const id = node?.data?.id;
        const shouldSelect = Array.isArray(selectedInsurance)
          ? selectedInsurance.includes(id)
          : false;
        if (node.isSelected() !== shouldSelect) node.setSelected(shouldSelect);
      });
    } catch (e) {
      // silence
    }
  }, [selectedInsurance, gridApiSelected]);

  return (
    <section className="container-x-large py-5">
      {(isFetching || isFetching1 || isFetching2) && <Spinner />}
      <ErrorMessage
        error={errors || error || error1 || error2}
        wrapperClassName="mb-2"
      />

      <InputField
        id="filterBy"
        label={labels?.productManufacturer?.filterByInsurance}
        wrapperClassName="col-md-3 mb-2"
        maxLength="35"
        change={(e) => {
          setFilterBy(e);
        }}
        value={filterBy}
      />

      <AgGrid
        wrapperClassNames="mb-3 d-none d-md-block"
        printClassNames="mt-md-n3"
        columnDefs={columnDefsForAll}
        rowSelection="multiple"
        rowData={filteredRowData}
        domLayout={"normal"}
        onSelectionChanged={onSelectionChangedForAll}
        onGridReady={onGridReadyForAll}
      />

      {isMobileView && <MobileResponsiveGrid
        key="prodManufacturerInsVehicle1"
        rowData={
         filteredRowData
        }
        columnDefs={mobileColumnDefsAll}
        getRowClass={getRowClassForAll}
        selectedRowId={selectedInsuranceFromAll}
        selectedRowIdField="id"
        onSelectionChanged={(ids) => setSelectedInsuranceFromAll(ids)}
      />
 }  
      
      <Button
        id="add"
        text={labels?.productManufacturer?.add}
        wrapperClassName="col-md-2 ms-auto mb-5 mt-4"
        click={addInsurance}
        disabled={!selectedInsuranceFromAll.length}
      />

      <Heading
        text={labels?.productManufacturer?.insuranceCompany}
        wrapperClassNames="h4 border-bottom-1 pb-2 mb-3 mt-4"
        title={false}
      />
      <AgGrid
        wrapperClassNames="mb-3 d-none d-md-block"
        columnDefs={columnDefs}
        rowSelection="multiple"
        rowData={rowData}
        domLayout={"normal"}
        onSelectionChanged={onSelectionChanged}
        onGridReady={onGridReadySelected}
        overlayNoRowsTemplate={overlayNoRowsTemplate}
      />

      <MobileResponsiveGrid
        key="prodManufacturerInsVehicle2"
        rowData={rowData}
        columnDefs={mobileColumnDefsSelected}
        getRowClass={getRowClassSelected}
        selectedRowId={selectedInsurance}
        selectedRowIdField="id"
        onSelectionChanged={(ids) => setSelectedInsurance(ids)}
      />

      <Button
        id="remove"
        text={labels?.productManufacturer?.remove}
        wrapperClassName="col-md-2 ms-auto mt-4"
        click={removeInsurance}
        disabled={!selectedInsurance.length}
      />

      <Button
        id="done"
        text={labels?.productManufacturer?.submit}
        wrapperClassName="col-md-2 mx-auto mt-2"
        click={updateInsuranceCompanies}
      />

      <SuccessPopup
        showModal={showPopup}
        buttonText={labels?.productManufacturer?.continue}
        click={cancel}
        body={labels?.productManufacturer?.requestCompleted}
      />
    </section>
  );
};

export default ProductManufacturerInsuranceCompanies;